// The two-axis autoplay lifecycle (client-audio spec R6 / design DD5; tasks.md 4.2). This
// module models the enable lifecycle as TWO independent axes — a durable preference
// (`enabled`, persisted in `audio-prefs-store`) and the runtime `AudioContext`
// (`Absent → Running ⇄ Suspended → Closed`) — and renders their product, including the
// load-bearing `enabled-but-locked` composite (pref on, no usable context yet, because
// browser autoplay policy forbids creating/resuming a context without a fresh gesture).
//
// Structure (DD5): a PURE reducer (`reduce`) computes the next state + a list of effects
// from an action, with no live `AudioContext` — that is what makes the whole autoplay
// contract (enabled-but-locked, first-tap-for-both-persisted-values, resume rejection,
// suspension reflection, and the two out-of-order stale-resume races) unit-testable in the
// `node` project without a browser. A thin controller (`createAudioLifecycle`) owns the
// mutable state, interprets the effects against the real engine + prefs, lazily constructs
// the graph on the first gesture, AWAITS `resume()`, retries only from another gesture on
// rejection, and reflects browser `statechange` suspension as locked.
//
// Stale-resume race guard (review-2 F3 / review-2c P3-F1): a monotonic action `generation`
// is bumped by BOTH a `tap` AND an invalidating `statechange` (to `suspended`/`closed`), and
// is captured on each pending `resume()`. A `resumeResolved`/`resumeRejected` is applied
// only when its generation is CURRENT *and* the live `AudioContext.state` is `running` — so
// neither a superseded re-tap (rapid double-tap) nor a suspension observed mid-resume can
// arm/unmute against the latest state. A tap while enabled-but-locked is ALWAYS an unlock
// attempt, never a disable; disable-by-tap happens only from a Running context.

import type { AudioContextLike, AudioEngine } from './engine';
import { NullAudioEngine, WebAudioEngine } from './engine';
import type { AudioPrefsStore } from './audio-prefs-store';

// --- rendered / runtime state types -------------------------------------------------------

/** The runtime `AudioContext` axis, plus `'absent'` for "no context constructed yet" (the
 * boot / enabled-but-locked case). The real context's three states are a subset. */
export type RuntimeContextState = 'absent' | 'suspended' | 'running' | 'closed';

/** The rendered lifecycle state — the product of the two axes (DD5). `locked` covers every
 * no-usable-running-context case (boot pref-off, enabled-but-locked, suspended-as-locked);
 * `playing`/`muted` distinguish an armed-and-Running context by the `enabled` pref. */
export type RenderState = 'locked' | 'playing' | 'muted';

/** The public snapshot the `SoundControl` renders. `enabled` + `contextState` let the
 * component pick the copy for the four visible affordances (enabled-but-locked vs
 * suspended-as-locked vs playing vs muted); `render` is the canonical three-value state. */
export interface LifecycleSnapshot {
  readonly enabled: boolean;
  readonly contextState: RuntimeContextState;
  readonly arming: boolean;
  readonly render: RenderState;
}

// --- pure reducer -------------------------------------------------------------------------

/** The reducer's internal state: the two axes plus the arming flag and the monotonic action
 * generation guarding out-of-order `resume()` settlements. */
export interface LifecycleState {
  readonly enabled: boolean;
  readonly contextState: RuntimeContextState;
  readonly arming: boolean;
  readonly generation: number;
}

/** The actions the reducer folds. `tap` is the user gesture; `statechange` is a browser
 * `AudioContext` transition; `resumeResolved`/`resumeRejected` are the awaited `resume()`
 * settlements carrying the generation they were issued at plus the live context state read
 * at settle time. */
export type LifecycleAction =
  | { readonly type: 'tap' }
  | { readonly type: 'statechange'; readonly state: RuntimeContextState }
  | { readonly type: 'resumeResolved'; readonly generation: number; readonly liveState: RuntimeContextState }
  | { readonly type: 'resumeRejected'; readonly generation: number; readonly liveState: RuntimeContextState };

/** The side effects the controller performs. The reducer stays pure; the controller
 * interprets these against the real prefs store + engine + `AudioContext`. */
export type LifecycleEffect =
  | { readonly type: 'persist-enabled'; readonly enabled: boolean }
  | { readonly type: 'set-muted'; readonly muted: boolean }
  | { readonly type: 'arm' };

/** The reducer output: the next state plus effects to perform. */
export interface ReduceResult {
  readonly state: LifecycleState;
  readonly effects: readonly LifecycleEffect[];
}

/** The boot state: the durable `enabled` from the preference, no context yet (autoplay
 * policy — a context is constructed only on the first gesture), generation 0. */
export function initialLifecycleState(enabled: boolean): LifecycleState {
  return { enabled, contextState: 'absent', arming: false, generation: 0 };
}

/** Derive the rendered three-value state from the two axes (DD5). A Running context renders
 * `playing`/`muted` by the `enabled` pref; anything else (absent/suspended/closed) is
 * `locked`. */
export function deriveRender(state: LifecycleState): RenderState {
  if (state.contextState === 'running') {
    return state.enabled ? 'playing' : 'muted';
  }
  return 'locked';
}

/** The pure two-axis reducer (DD5). No live `AudioContext` — every runtime fact enters via
 * an action, so the full autoplay contract is deterministically testable. */
export function reduce(state: LifecycleState, action: LifecycleAction): ReduceResult {
  switch (action.type) {
    case 'tap': {
      const generation = state.generation + 1;
      if (state.contextState === 'running') {
        // Armed + Running: a tap toggles the mute/enable pref (never a re-lock, spec R6).
        const enabled = !state.enabled;
        return {
          state: { ...state, enabled, arming: false, generation },
          effects: [
            { type: 'persist-enabled', enabled },
            { type: 'set-muted', muted: !enabled },
          ],
        };
      }
      // Locked (absent/suspended/closed): a tap is ALWAYS an unlock attempt — it enables
      // (from disabled) or stays enabled (enabled-but-locked / suspended), never a disable.
      // Either way the context is suspended until `resume()` settles it to running: a
      // freshly constructed / rebuilt (post-close) context boots suspended, and an existing
      // suspended context stays suspended — so the reducer reflects `suspended` uniformly.
      return {
        state: { enabled: true, contextState: 'suspended', arming: true, generation },
        effects: [
          { type: 'persist-enabled', enabled: true },
          { type: 'arm' },
        ],
      };
    }
    case 'statechange': {
      // A browser-driven context transition is always current truth. A move to
      // `suspended`/`closed` is INVALIDATING: it reflects locked AND bumps the generation so
      // any in-flight `resume()` from before the suspension is discarded when it settles.
      const invalidating = action.state === 'suspended' || action.state === 'closed';
      return {
        state: {
          ...state,
          contextState: action.state,
          arming: false,
          generation: invalidating ? state.generation + 1 : state.generation,
        },
        effects: [],
      };
    }
    case 'resumeResolved': {
      if (action.generation !== state.generation) {
        return { state, effects: [] }; // superseded / stale — discard, never arm
      }
      if (action.liveState !== 'running') {
        // Generation current but the live context is not running (e.g. suspended mid-resume)
        // — clear the arming flag but do NOT arm against a non-running context.
        return { state: { ...state, arming: false }, effects: [] };
      }
      return { state: { ...state, contextState: 'running', arming: false }, effects: [] };
    }
    case 'resumeRejected': {
      if (action.generation !== state.generation) {
        return { state, effects: [] }; // superseded / stale — discard
      }
      // Stay locked; schedule NO timer retry — only a fresh gesture re-attempts `resume()`.
      return { state: { ...state, arming: false }, effects: [] };
    }
    default: {
      // Exhaustiveness guard — an unknown action leaves state untouched.
      return { state, effects: [] };
    }
  }
}

// --- controller ---------------------------------------------------------------------------

/** A listener notified with the latest snapshot whenever the rendered state changes. */
export type LifecycleListener = (snapshot: LifecycleSnapshot) => void;

/** The public lifecycle handle the `SoundControl` (group 6) and `initAudio` (group 7)
 * consume: a reactive snapshot, the single `tap` gesture, a subscribable, the current engine
 * (swapped from `NullAudioEngine` to `WebAudioEngine` on first arm), and teardown. */
export interface AudioLifecycle {
  /** The current rendered snapshot (a fresh value each call). */
  getState(): LifecycleSnapshot;
  /** The one user gesture: unlock (locked) or mute/unmute (Running). */
  tap(): void;
  /** Subscribe to snapshot changes; returns an unsubscribe. */
  subscribe(listener: LifecycleListener): () => void;
  /** The current engine — `NullAudioEngine` before the first unlock, then `WebAudioEngine`. */
  getEngine(): AudioEngine;
  /** Detach the `statechange` listener and dispose the engine; idempotent. */
  dispose(): void;
}

export interface AudioLifecycleDeps {
  /** The durable preference adapter (the `enabled`/`masterVolume` source of truth, R7). */
  readonly prefs: AudioPrefsStore;
  /** Constructs a fresh `AudioContext` (or a stub in tests) on the first gesture / after a
   * close. Injected so no test ever constructs a real `AudioContext` (spec R1 seam). */
  readonly createContext: () => AudioContextLike;
  /** Builds the engine over a context. Defaults to a real `WebAudioEngine` at the pref's
   * master volume; tests may inject a recording double. */
  readonly createEngine?: (ctx: AudioContextLike) => AudioEngine;
  /** Notified with the `WebAudioEngine` each time the graph is (re)constructed — the
   * `NullAudioEngine` → `WebAudioEngine` swap the driver keys its bed activation on (DD3). */
  readonly onEngineArmed?: (engine: AudioEngine) => void;
}

/** The lifecycle's runtime `AudioContext.state` (`'suspended' | 'running' | 'closed'`) is
 * already a subset of `RuntimeContextState`; this is an identity narrowing kept explicit at
 * the boundary between the injected context and the reducer's state type. */
function fromContextState(state: 'suspended' | 'running' | 'closed'): RuntimeContextState {
  return state;
}

/**
 * Build the lifecycle controller (DD5). It holds the reducer state, lazily constructs the
 * graph on the first `arm` effect, awaits `resume()` (dispatching a generation-stamped
 * settlement), attaches a `statechange` listener (browser suspension → locked), and
 * interprets `persist-enabled`/`set-muted` against the injected prefs + engine.
 */
export function createAudioLifecycle(deps: AudioLifecycleDeps): AudioLifecycle {
  const createEngine =
    deps.createEngine ??
    ((ctx: AudioContextLike): AudioEngine =>
      new WebAudioEngine(ctx, { masterVolume: deps.prefs.get().masterVolume }));

  let state: LifecycleState = initialLifecycleState(deps.prefs.get().enabled);
  let engine: AudioEngine = new NullAudioEngine();
  let ctx: AudioContextLike | undefined;
  let statechangeListener: (() => void) | undefined;
  let disposed = false;
  const listeners = new Set<LifecycleListener>();

  function snapshot(): LifecycleSnapshot {
    return {
      enabled: state.enabled,
      contextState: state.contextState,
      arming: state.arming,
      render: deriveRender(state),
    };
  }

  function notify(): void {
    const current = snapshot();
    for (const listener of [...listeners]) {
      listener(current);
    }
  }

  /** Ensure a live (non-closed) context + engine exist, constructing lazily on the first
   * gesture and rebuilding after a close. Attaches the `statechange` listener and announces
   * the `NullAudioEngine` → `WebAudioEngine` swap. Returns `false` when no usable context could
   * be constructed (a no-Web-Audio environment / a construction throw), leaving the engine as the
   * `NullAudioEngine` so the first tap degrades to locked rather than crashing (spec R1 /
   * implementation-review F3). */
  function ensureContext(): boolean {
    if (ctx !== undefined && state.contextState !== 'closed') {
      return true; // a live suspended/running context is reused, not rebuilt
    }
    let created: AudioContextLike;
    try {
      created = deps.createContext();
      engine = createEngine(created);
    } catch {
      // No usable AudioContext (absent constructor, or a construction failure): stay inert on
      // the NullAudioEngine, attach no listener, announce no arm. The caller reflects locked.
      return false;
    }
    ctx = created;
    const listener = (): void => dispatch({ type: 'statechange', state: fromContextState(created.state) });
    created.addEventListener('statechange', listener);
    statechangeListener = listener;
    deps.onEngineArmed?.(engine);
    return true;
  }

  /** The `arm` effect: construct-if-needed, ensure the engine is unmuted for the enabled
   * pref, then await `resume()` and dispatch a generation-stamped settlement carrying the
   * live context state read at settle time (the double guard, DD5). */
  function performArm(generation: number): void {
    if (!ensureContext()) {
      // No usable context: reflect locked (clear the arming flag) and retry only from a fresh
      // gesture — never a crash out of the tap handler (implementation-review F3).
      dispatch({ type: 'resumeRejected', generation, liveState: 'suspended' });
      return;
    }
    const armedCtx = ctx;
    if (armedCtx === undefined) {
      return;
    }
    // Unlock arms audio for the enabled pref (unmutes a context reused after a muted
    // suspension; a freshly built engine is already unmuted, so this is a safe no-op there).
    engine.setMuted(!state.enabled);
    engine.resume().then(
      () => dispatch({ type: 'resumeResolved', generation, liveState: fromContextState(armedCtx.state) }),
      () => dispatch({ type: 'resumeRejected', generation, liveState: fromContextState(armedCtx.state) }),
    );
  }

  function applyEffect(effect: LifecycleEffect): void {
    switch (effect.type) {
      case 'persist-enabled':
        deps.prefs.set({ enabled: effect.enabled });
        break;
      case 'set-muted':
        engine.setMuted(effect.muted);
        break;
      case 'arm':
        performArm(state.generation);
        break;
    }
  }

  function dispatch(action: LifecycleAction): void {
    if (disposed) {
      return;
    }
    const result = reduce(state, action);
    state = result.state;
    for (const effect of result.effects) {
      applyEffect(effect);
    }
    notify();
  }

  return {
    getState: snapshot,
    tap(): void {
      dispatch({ type: 'tap' });
    },
    subscribe(listener: LifecycleListener): () => void {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    getEngine(): AudioEngine {
      return engine;
    },
    dispose(): void {
      if (disposed) {
        return;
      }
      disposed = true;
      if (ctx !== undefined && statechangeListener !== undefined) {
        ctx.removeEventListener('statechange', statechangeListener);
      }
      engine.dispose();
      listeners.clear();
    },
  };
}
